/**
 * @fileoverview Utility functions for game logic.
 * Handles matching pairs setup, distance calculations, and storage operations.
 */

import { CONFIG } from '../core/GameConfig.js';
import { ThemeService } from '../services/ThemeService.js';
import { BaseTheme } from '../themes/abstract/BaseTheme.js';
import { StorageService } from '../services/StorageService.js';

export type ShapeColor = { fill: string; stroke: string };
export interface MatchingPair {
    dsIndex: number;
    tsIndex: number;
    color: ShapeColor ;
    labelValue: number | string; // label
    completed: boolean;
    shapeType?: string;
}

/**
 * Position configuration for draggable and target shapes.
 */

export interface ShapePositions {
    DS_POSITIONS: { x: number; y: number; }[];
    TS_POSITIONS: { x: number; y: number; }[];
}


/**
 * Utility functions for the toddler game.
 * All methods are static and stateless.
 */
export class GameUtils {
    /**
     * Calculate Euclidean distance between two Konva objects.
     * @param {Konva.Node} obj1 - First Konva object with x() and y() methods
     * @param {Konva.Node} obj2 - Second Konva object with x() and y() methods
     * @returns {number} Distance in pixels
     */
    static calculateDistance(obj1: any, obj2: any): number {
        const dx = obj1.x() - obj2.x();
        const dy = obj1.y() - obj2.y();
        return Math.sqrt(dx * dx + dy * dy);
    }
    

    
    /**
     * Save matching pairs configuration to storage.
     */
    static saveMatchingPairs(matchingPairs: MatchingPair[], theme: BaseTheme) {
        StorageService.getInstance().saveMatchingPairs(matchingPairs, theme);
    }
    
    /**
     * Load matching pairs configuration from storage.
     * @returns {MatchingPair[] | null} Saved matching pairs or null if not found/invalid
     */
    static loadMatchingPairs(isPremiumUser: boolean): MatchingPair[] | null {
        const currentTheme = ThemeService.getCurrentThemeFromStorage(isPremiumUser);
        return StorageService.getInstance().loadMatchingPairs(currentTheme.id, currentTheme.numPairs);
    }
    
    /**
     * Save game progress (which pairs are completed) to storage.
     * @param {MatchingPair[]} matchingPairs - Array of matching pair configurations
     */
    static saveProgress(matchingPairs: MatchingPair[]) {
        StorageService.getInstance().saveProgress(matchingPairs);
    }
    
    /**
     * Load game progress from storage.
     * @returns {{completedPairs: number[], completedCount: number}} Progress data
     */
    static loadProgress(): { completedPairs: number[]; completedCount: number; } {
        return StorageService.getInstance().loadProgress();
    }
    
    /**
     * Clear saved progress from storage.
     */
    static clearProgress() {
        StorageService.getInstance().clearProgress();
    }
    
    /**
     * Clear saved matching pairs configuration and progress from storage.
     * Call this to force generation of new random configuration.
     */
    static clearSavedConfiguration() {
        StorageService.getInstance().clearSavedConfiguration();
    }
    
    /**
     * Setup random matching pairs for DS and TS.
     * Tries to load from storage first, generates new configuration if not found.
     * @param doShuffle - If true, shuffles target positions. If false, keeps labels in order.
     */
    static setupMatchingPairs(isPremiumUser: boolean, doShuffle: boolean = true): MatchingPair[] {
        const savedPairs = GameUtils.loadMatchingPairs(isPremiumUser);
        if (savedPairs) { // && savedPairs[0].labelValue != null) {
            console.log('Loaded saved matching pairs configuration');
            return savedPairs;
        }
        
        const currentTheme = ThemeService.getCurrentThemeFromStorage(isPremiumUser);
        const numPairs = currentTheme.numPairs;
        console.log(`Creating ${numPairs} pairs for theme: ${currentTheme.name} (shuffle: ${doShuffle})`);
        
        // Delegate to theme for all selection logic
        const selectedColors = currentTheme.selectColors(CONFIG.DRAGGABLE_COLORS, numPairs);
        const selectedSymbols = currentTheme.selectLabels(numPairs, doShuffle);
        
        // Conditionally shuffle target positions
        const shuffledTsIndices = doShuffle 
            ? currentTheme.shuffleTargetPositions(numPairs)
            : Array.from({ length: numPairs }, (_, i) => i); // Sequential order [0, 1, 2, 3, ...]
        
        const selectedShapeTypes = currentTheme.selectShapeTypes(numPairs, doShuffle);
        
        // Build matching pairs
        const matchingPairs: MatchingPair[] = Array.from({ length: numPairs }, (_, i) => ({
            dsIndex: i,
            tsIndex: shuffledTsIndices[i],
            color: selectedColors[i],
            labelValue: selectedSymbols[i],
            shapeType: selectedShapeTypes[i],
            completed: false
        }));
        
        GameUtils.saveMatchingPairs(matchingPairs, currentTheme);
        return matchingPairs;
    }
    

}

